import React from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { 
  SiteFooterLayout, LayoutShell, LangStringsProvider, ViewProvider, useLang, 
  TrendCard, TopicCard, EntityCard, PageCard, EmbedComponentsProvider,
  createEmbed, useEmbedComponents, OutlineEntityDotLink, EntityDotLink, 
  useReady, CitationCard, PersonValue, OrganisationValue, EntityCell, EntityTypeBannersProvider, 
  TopicExpertiseValue, useContributeForm, PersonBanner, UiProvider, UiConfig, 
  FeedEntryCard,
  PageHalfBanner,
  withLightMode,
  EntityExplorerLayoutWithoutBanner,
  UserMenuProvider,
  EntityExplorerLayout,
  useModal,
  EntityCellProps,
  LinkTargetFieldsProvider,
} from '@shapeable/ui';

import * as pageLayouts from './page-layouts';
import * as sliceLayouts from './slice-layouts';
import { LANG_STRINGS } from '../lang';
import { AQUA_BLUE, GESDA_GREEN, GESDA_PURPLE, GESDA_PURPLE_DARK, GESDA_PURPLE_LIGHT, LayoutStyles, themeOverrides } from '../theme';
import { SubTopicsProvider } from '../gatsby/providers/sub-topics-provider';
import { TopicsProvider } from '../gatsby/providers/topics-provider';
import { TrendsProvider } from '../gatsby/providers/trends-provider';
import { TrendsRadarProvider } from '../gatsby/providers/trends-radar-provider';
import { PagesProvider } from '../gatsby/providers/pages-provider';
import { EditionsProvider } from '../gatsby/providers/editions-provider';
import { IndicatorGroupLayoutPspChart, IndicatorLayoutPspOverlayDot, IndicatorLayoutPspSector, RadarConfig, RadarProvider } from '@shapeable/gesda-ui';
import { DebateCard } from './entities/debate-card';
import { OrganisationsProvider } from '../gatsby/providers/organisations-provider';
import { PeopleProvider } from '../gatsby/providers/people-provider';
import { FeedEntriesProvider } from '../gatsby/providers/feed-entries-provider';
import { FeedEntryTypesProvider } from '../gatsby/providers/feed-entry-types-provider';
import { EventsProvider } from '../gatsby/providers/events-provider';
import { EventBlockquote } from './entities/event-blockquote';
import { MapConfig, MapProvider, MapState } from '@shapeable/maps';
import { DEFAULT_MAP_ENTITY_TYPE_NAMES } from '../data';
import { CitationIcon, PersonIcon } from '@shapeable/icons';
import { SidebarButtons } from './elements/sidebar-buttons';
import { EntityMapPanel } from './entities/entity-map-panel';
import { EntityTypesProvider } from '../gatsby/providers/entity-types-provider';
import { SubTopicOutlineDotLink } from './entities/sub-topic-outline-dot-link';
import { SubTopicAnchorCard } from './entities/sub-topic-anchor-card';
import { LinkCard } from './entities/link-card';
import { OutlineEntityLinkEmbed } from './entities/outline-entity-link-embed';
import { VideosProvider } from '../gatsby/providers/videos-provider';
import { TopicBannerLayout } from './entities/topic-banner-layout';
import { PersonCard } from './entities/person-card';
import { ContributeFormPanel } from './elements/contribute-form-panel';
import { CitationsProvider } from '../gatsby/providers/citations-provider';
import { PersonTooltip } from './entities/person-tooltip';
import * as EntityPageQueries from '../graphql/details';
import { UserMenuSpaceItem } from './elements/user-menu-space-item';
import { AiChatInterfaceModal } from './elements/ai-chat-interface-modal';
import { useSubTopics } from '../hooks/use-sub-topics';
import { usePeople } from '../hooks/use-people';


const initialMapState: MapState = {
  isVisible: false,
  isInitialized: false,
  zoom: 2,
  entityTypeNames: DEFAULT_MAP_ENTITY_TYPE_NAMES,
  latitude: 46.8182,
	longitude: 8.2275,
};

export type LayoutPropTypes = Classable & HasChildren & {
  path?: string;
  data?: any;
};

const LayoutDefaultProps: LayoutPropTypes = {
};

type ContributeFormPanelProps = {
  isVisible?: boolean;
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    ${LayoutStyles};
  `,
});

const FooterStyles = breakpoints({
  base: css`
  `,
});

const BodyStyles = breakpoints({
  base: css`
    flex-grow: 1;
  `,
});

const PersonIconMapStyles = breakpoints({
  base: css`
    ${theme.FILL('bright')}
    background-color: ${theme.COLOR('dark')};
    border-radius: 50%;
  `,
});

const ContributeFormStyles = breakpoints({
  base: css`
    position: fixed;
    left: 100%;
    z-index: 800;
    transition: left 0.3s;
    width: 100%;
    height: 100%;
    top: 0;

    ${({ isVisible }: ContributeFormPanelProps) => isVisible && css`
      left: 0%;
    `}
  `,
  tablet: css`

  `,
  desktop: css`
    width: 50%;

    ${({ isVisible }: ContributeFormPanelProps) => isVisible && css`
      left: 50%;
    `}
  `,
});

const MapStyles = breakpoints({
  base: css`
    
  `,
});

const PageCardEmbedStyles = breakpoints({
  base: css`
    margin: ${theme.UNIT(5)} 0 ${theme.UNIT(10)};
  `,
});



// -------- Components -------->

const My = {
  Container: styled(LayoutShell)`${ContainerStyles}`,
    Body: styled.div`${BodyStyles}`,
    Footer: styled(SiteFooterLayout)`${FooterStyles}`,
    PersonIconMap: styled(PersonIcon)`${PersonIconMapStyles}`,
    Map: styled(EntityMapPanel)`${MapStyles}`,
    ContributeForm: styled(ContributeFormPanel)<ContributeFormPanelProps>`${ContributeFormStyles}`,

    PageCardEmbed: styled(EntityCell)`${PageCardEmbedStyles}`,
};

export const Layout: React.FC<LayoutPropTypes> = (props) => {
  const { className, children, path } = props;

  const [isOnboardingLayout, setIsOnboardingLayout] = React.useState(false);

  React.useEffect(() => {
    setIsOnboardingLayout(window.location.pathname === '/app/welcome');
  }, []);

  const pathname = path || (typeof window !== 'undefined' ? window.location.pathname : '');
  const isPdfRoute = pathname.indexOf('/pdf/') === 0;

  const embedComponents = useEmbedComponents();

  const contributeForm = useContributeForm();

  const t = useLang();
  const modal = useModal();

  const { isReady } = useReady();

  const onHeaderButtonClick = (slug: string) => {
    if (slug === 'ai-modal-button') {
      modal?.show(<AiChatInterfaceModal />, { color: '#000000', title: 'RadarAI', opacity: 0.5 });
    }
  };

  const mapConfig: MapConfig = {
    initialMapState,
    availableEntityTypeNames: ['Person', 'Organisation'], 
  }

  const config: UiConfig = {
    sidebar: {
      isAvailable: () => true,
      showFullscreenButton: true,
      buttons: <SidebarButtons />,
      color: '#292929',
    },
    header: {
      showThemeMode: false,
      boundary: 'feature-slim',
      // utilityMenu: utlityMenuItems,
      // showUtilities: true,
      variant: 'default',
      onHeaderButtonClick,
    },
    explorer: {
      defaultView: 'radar',
      authenticationRequired: false,
      siteHeaderProvider: {
        boundary: 'feature-slim',
      },
    },
    componentProps: {
      SiteMenuBar: {
        useRoundedCorners: true,
        colors: {
          background: GESDA_PURPLE_DARK,
        }
      },
      SiteHeaderLayout: {
        useRoundedCorners: true,
        roundedCornerFillColor: 'brightWarm',
        utilityButtonsPlacement: 'inline',
        utilityButtonsVariant: 'default',
        searchUtilityButtonShowLabel: false,
        utilityUserButtonVariant: 'text',
        menuBarUtilityUserButtonVariant: 'text',
        utilityButtonsCompactBreakpoint: 'desktopMid',
        utilityButtonGradient: { from: GESDA_PURPLE_LIGHT, to: GESDA_GREEN },
        colors: {
          background: GESDA_PURPLE_DARK,
          text: 'light',
        },
        menuAnimation: true,
      },
      SliceOnboardingTrendExpertiseGrid: {
        expertiseLevel: 'interested-novice',
      },
      TaskItemLayout: {
        hideContent: false,
      },
      MenuItem: {
        colors: {
          hover: {
            text: 'strong',
            background: GESDA_PURPLE,
          }
        }
      },
      MenuItemLink: {
        colors: {
          hover: {
            text: AQUA_BLUE,
          },
          active: {
            text: AQUA_BLUE,
          }
        }
      },
      SessionLayout: {
        photoStyle: 'original',
      },
      NavigationMenuItem:{
        colors: {
          active: {
            text: AQUA_BLUE,
          }
        }
      },
      ExplorerEntityDetailsLayout: {
        stickyTopOffset: 80,
        isAsideSticky: true,
        asideIsOffset: false,
      },
    },
    showCookieBanner: isPdfRoute ? false : true,
    menuBar: {
      boundary: 'feature-slim',
      showShadow: false,
      delay: 0,
      slideDownDuration: 0.2,
    },
    theme: themeOverrides,
    layouts: {
      pageLayouts, 
      defaultPageLayout: pageLayouts.PageLayoutDefault,
      sliceLayouts,
      indicatorGroupLayouts: {
        IndicatorGroupLayoutPspChart,
      },
      indicatorLayouts: {
        IndicatorLayoutPspOverlayDot,
        IndicatorLayoutPspSector,
      },
    },
    search: {
      placeholder: 'Search GESDA Radar',
      isAvailable: true,
      isVisible: true,
      extraDataHooks: {
        SubTopic: useSubTopics,
      }
    },
    entity: {
      dataProviders:  [
        SubTopicsProvider, TopicsProvider, TrendsProvider,
        TrendsRadarProvider, PagesProvider, OrganisationsProvider,
        PeopleProvider, FeedEntriesProvider, FeedEntryTypesProvider, EventsProvider, EntityTypesProvider,
        VideosProvider, EditionsProvider, CitationsProvider,
      ],
      layouts: {
        Person: EntityExplorerLayoutWithoutBanner,
        Space: EntityExplorerLayout,
      },
      labels: {
        Trend: 'Theme',
        Topic: 'Topic',
        SubTopic: 'Sub-Field',
      },
      dataHooks: {
        SubTopic: useSubTopics,
        Person: usePeople,
      },
      cards: {
        Trend: TrendCard,
        Topic: TopicCard,
        Post: EntityCard,
        SubTopic: SubTopicAnchorCard,
        Page: PageCard,
        FeedEntry: FeedEntryCard,
        Debate: DebateCard,
        InvitedContribution: EntityCard,
        LandmarkReport: EntityCard,
        Citation: CitationCard,
        Link: LinkCard,
        Person: PersonCard,
        // FileAsset: FileAssetCardRow,
      },
      pageQueries: EntityPageQueries,
      tooltips: {
        Person: PersonTooltip,
      },
      values: {
        Trend: OutlineEntityDotLink,
        Topic: OutlineEntityDotLink,
        SubTopic: SubTopicOutlineDotLink,
        Person: PersonValue,
        Page: EntityDotLink,
        TopicExpertise: TopicExpertiseValue,
        Organisation: OrganisationValue,
        Event: OutlineEntityDotLink,
      },
      icons: {
        Edition: CitationIcon,
      },
    },
    entityType: {
      Person: {
        filters: {
          fieldNames: [
            { internalName: 'Edition', fieldName: 'latestEdition', label: 'Editions' },
            { internalName: 'Trend', fieldName: 'trends', label: 'Themes' },
            { internalName: 'Topic', fieldName: 'topics', label: 'Topics' },
            { internalName: 'Economy', fieldName: 'country', label: 'Country' }
          ]
        }
      },
    },
  };

  const radarConfig: RadarConfig = {
    topicNodes: {
      "precision-health": {
        offsetY: -3,
        offsetX: 25,
        textAnchor: 'start',
      },
      "behavioural-science-of-groups": {
        offsetY: 33,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "engineering-human-biology": {
        offsetY: -5,
        offsetX: -25,
        textAnchor: 'end',
      },
      "human-applications-of-genetic-engineering": {
        offsetY: -20,
        offsetX: 30,
        textAnchor: 'start',
      },
      "advanced-therapeutics": {
        offsetY: -40,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "quantum-computing": {
        offsetY: -40,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "quantum-effects-in-biology": {
        offsetY: -50,
        offsetX: 15,
        textAnchor: 'start',
      },
      "future-therapeutics": {
        offsetY: -5,
        offsetX: 25,
        textAnchor: 'start',
      },
      "food-systems-engineering": {
        offsetY: -2,
        offsetX: -25,
        textAnchor: 'end',
      },
      "future-of-consciousness": {
        offsetY: -37,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "synthetic-biology-1": {
        offsetY: -3,
        offsetX: 25,
        textAnchor: 'start',
      },
      "orbital-environment": {
        offsetY: -3,
        offsetX: 25,
        textAnchor: "start", 
      },
      "science-of-the-origins-of-life-1": {
        offsetY: -15,
        offsetX: -25,
        textAnchor: 'end', 
      },
      "science-based-diplomacy": {
        offsetY: 15,
        offsetX: -25,
        textAnchor: 'end',
      },
      "ocean-science": {
        offsetY: 6,
        offsetX: 25,
        textAnchor: "start",
      },
      "digital-security": {
        offsetX: 0,
        offsetY: -40,
        textAnchor: "middle",
      },
      "pathogen-biology": {
        offsetY: 35,
        offsetX: 0,
        textAnchor: 'end',
      },
      "space-resources": {
        offsetY: -10,
      },
      "organoids": {
        offsetY: 5,
        offsetX: 28,
      },
      "healthspan-extension": {
        offsetY: 35,
        offsetX: 15,
        textAnchor: 'end',
      },
      "democracy-affirming-technologies": {
        offsetY: 20,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "archaeology": {
        offsetY: 32,
        offsetX: -25,
        textAnchor: 'middle',
      },
      "cognitive-enhancement": {
        offsetY: 35,
        offsetX: -25,
        textAnchor: 'start',
      },
      "collective-intelligence": {
        offsetY: -40,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "extended-reality": {
        offsetY: -3,
        offsetX: 25,
        textAnchor: 'start',
      },
      "robotics-and-embodied-intelligence": {
        offsetY: -15,
        offsetX: 25,
        textAnchor: 'start',
      },
      "futures-research": {
        offsetY: -2,
        offsetX: 25,
        textAnchor: 'start',
      },
      "energy-systems": {
        offsetY: 5,
        offsetX: 25,
        textAnchor: 'start',
      },
      "language-and-communication": {
        offsetY: 20,
        offsetX: -20,
        textAnchor: 'end',
      },
      "mathematics": {
        offsetY: -25,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "cell-and-gene-engineering": {
        offsetY: 35,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "economics": {
        offsetY: 33,
        offsetX: 0,
        textAnchor: 'middle',
      },
      "fungal-biology": {
        offsetY: 35,
        offsetX: -25,
        textAnchor: 'start',
      },
      "ecology": {
        offsetY: 6,
        offsetX: 25,
        textAnchor: 'start',
      },
       "augmenting-consciousness": {
        offsetY: -40,
        offsetX: 10,
        textAnchor: 'end',
       },
    },
    viewboxPresets: {
      "human-augmentation": {
        x: 63.33192511596794,
        y: -24.16983724359839,
        width: 516.0960000000001,
        height: 516.0960000000001,
        zoom: 0.612
      },

      "eco-regeneration-geoengineering": {
        x: 482.24218418554875,
        y: 3.2851691647673786,
        width: 516.0960000000001,
        height: 516.0960000000001,
        zoom: 0.612
      },
      
      "science-and-diplomacy": {
        x: 483.12782955355846,
        y: 456.73559758680847,
        width: 516.0960000000001,
        height: 516.0960000000001,
        zoom: 0.612
      },

      "knowledge-foundations": {
        x: 240.46099871832624,
        y: 571.8694954283408,
        width: 516.0960000000001,
        height: 516.0960000000001,
        zoom: 0.612
      },

      "quantum-revolution-and-advanced-ai": {
        x: -1.3201867488945305,
        y: 369.05670615363897,
        width: 516.0960000000001,
        height: 516.0960000000001,
        zoom: 0.612
      },
    }
  };

  return (
    <LangStringsProvider value={LANG_STRINGS}>
    <ViewProvider>
    <UiProvider value={config}>
    <RadarProvider value={radarConfig}>
    {/* <OnboardingExpertiseLevelProvider value={{
      expertiseLevel: 'interested-novice',
    }}> */}
    <UserMenuProvider value={{
      userMenuBottomItem: UserMenuSpaceItem,
    }}>
    <EntityTypeBannersProvider value={{
      Topic: TopicBannerLayout,
      Person: PersonBanner,
      Event: withLightMode(PageHalfBanner),
      Page: withLightMode(PageHalfBanner),
      Space: withLightMode(PageHalfBanner),
    }}>
    <LinkTargetFieldsProvider value={['topic']}>
    <EmbedComponentsProvider value={{
      ...embedComponents,
      blockquote: EventBlockquote,
      Trend: createEmbed('trends', OutlineEntityLinkEmbed),
      Topic: createEmbed('topics', OutlineEntityLinkEmbed),
      SubTopic: createEmbed('subTopics', OutlineEntityLinkEmbed),
      Page: createEmbed('pages', My.PageCardEmbed, (props): Omit<EntityCellProps, 'entity'> => ({ style: { textDecoration: 'none', hyphens: 'none' }, useBoxShadow: true })),
      }}>
      <MapProvider value={mapConfig}>
      {
        isPdfRoute ? children : (
      <My.Container
        className={className}
        hideOverflow={false}
        includeTooltip
        tooltipProps={{
          backgroundColor: '#FFFFFF',
          className: 'shp--react-tooltip',
        }}
      >
        { 
          isReady && <My.Map /> 
        }
        {
          isReady && <My.ContributeForm isVisible={contributeForm.isVisible} />
        }
        <My.Body>
          {children}
        </My.Body>
        {
          isOnboardingLayout && 
          <My.Footer />
        }
      </My.Container>
        )
      }
      </MapProvider>
      </EmbedComponentsProvider>
      </LinkTargetFieldsProvider>
      </EntityTypeBannersProvider>
      </UserMenuProvider>
      {/* </OnboardingExpertiseLevelProvider> */}
    </RadarProvider>
    </UiProvider>
    </ViewProvider>
    </LangStringsProvider>
  );
  
  
};

Layout.defaultProps = LayoutDefaultProps;